import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';

/** A decorative, lazy-loaded WebGL sculpture. The page never depends on WebGL. */
export type SceneKind = 'orbit' | 'trophy' | 'gem' | 'coin';

export function Scene3D({
  className,
  violet = false,
  kind = 'orbit',
}: {
  className?: string;
  violet?: boolean;
  kind?: SceneKind;
}) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = container.current;
    if (!host || typeof IntersectionObserver === 'undefined') return;

    let disposed = false;
    let cleanup: (() => void) | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void import('./create-scene')
          .then(({ createScene }) => {
            if (!disposed) cleanup = createScene(host, violet, kind);
          })
          .catch(() => {
            /* The static brand mark remains when WebGL is unavailable. */
          });
      },
      { rootMargin: '150px' },
    );
    observer.observe(host);

    return () => {
      disposed = true;
      observer.disconnect();
      cleanup?.();
    };
  }, [violet, kind]);

  return (
    <div
      ref={container}
      aria-hidden="true"
      className={cn(
        'scene-3d',
        kind === 'coin' && 'scene-coin',
        violet && 'scene-violet',
        className,
      )}
    >
      <div className="scene-halo" />
      <img
        src={violet ? '/moon-logo.svg' : '/gee-logo.png'}
        alt=""
        width={881}
        height={519}
        className={cn('scene-wordmark', kind !== 'orbit' && 'scene-fallback')}
      />
    </div>
  );
}
